import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const googleClientId = useRef(null);
  const configLoaded = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Google Client ID from backend on mount
  useEffect(() => {
    const fetchGoogleConfig = async () => {
      try {
        const response = await fetch(`${API}/auth/google/config`);
        if (response.ok) {
          const data = await response.json();
          googleClientId.current = data.client_id;
          configLoaded.current = true;
          console.log('Google OAuth config loaded');
        } else {
          console.error('Failed to fetch Google config:', response.status);
        }
      } catch (error) {
        console.error('Failed to fetch Google config:', error);
      }
    };
    fetchGoogleConfig();
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the code and establish the session first
    const params = new URLSearchParams(window.location.search);
    if (params.get('code') && window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async () => {
    // If config hasn't loaded yet, try fetching it now
    if (!googleClientId.current) {
      try {
        const response = await fetch(`${API}/auth/google/config`);
        if (response.ok) {
          const data = await response.json();
          googleClientId.current = data.client_id;
        }
      } catch (error) {
        console.error('Failed to fetch Google config on login:', error);
      }
    }

    const clientId = googleClientId.current;
    if (!clientId) {
      alert('Unable to connect to authentication service. Please try again.');
      return;
    }

    const redirectUri = window.location.origin + '/auth/callback';
    const scope = 'openid email profile';
    
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scope,
      access_type: 'offline',
      prompt: 'select_account'
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    window.location.href = '/';
  };

  const setUserData = (userData) => {
    setUser(userData);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUserData, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
